import { Box, Center, Flex, Text } from '@chakra-ui/react';
import {
  AddPulsIcon,
  Avatar,
  ButtonGray,
  EditRoundIcon,
  LinkIcon,
  UploadAvatar,
  ExpandableText,
  LeaveIcon,
  EditIcon,
  SuccessIcon,
} from '@halo/ui-chat-react';
import type { GroupInfo, GroupMyRole, User } from '@halo/chat-sdk';
import {
  GroupMemberJoinMode,
  isSessionCategory,
  SessionCategoryPrefix,
  StateEnum,
  type Session,
} from '@halo/chat-sdk';
import { useTranslation } from 'react-i18next';

export type SetGroupInviteData = {
  session: Session | null;
  groupInfo: GroupInfo | null;
};

export interface MessageInviteCardProps {
  session?: Session | null;
  groupCreator?: User | undefined;
  groupInviter?: User | undefined;
  groupMyRole?: GroupMyRole;
  setIsOpen: (isOpen: boolean, data?: SetGroupInviteData | null) => void;
  onAvatarUpdate: (file: File) => Promise<void>;
  isNormalGroup: boolean;
  isGroupAdmin: boolean;
  isGroupCreator: boolean;
  isStrangerInvite: boolean;
  onStayClick: () => void;
  onExitGroupClick: () => void;
  onAddDescription: () => void;
  description?: string;
  infoEditDisabled: boolean;
  groupInfo: GroupInfo | null;
  onGroupLinkClick: () => void;
  onSettingClick: () => void;
}

export const MessageInviteCard = memo(function MessageInviteCard({
  groupCreator,
  groupInviter,
  groupMyRole,
  session,
  setIsOpen,
  onAvatarUpdate,
  isNormalGroup,
  isGroupAdmin,
  isGroupCreator,
  isStrangerInvite,
  onAddDescription,
  description,
  infoEditDisabled,
  groupInfo,
  onGroupLinkClick,
  onSettingClick,
  onStayClick,
  onExitGroupClick,
}: MessageInviteCardProps) {
  const { t } = useTranslation();
  const isGroup = isSessionCategory(session?.chat_id, SessionCategoryPrefix.Group);

  const handleAvatarUpdate = useCallback(
    async (file: File) => {
      await onAvatarUpdate(file);
    },
    [onAvatarUpdate]
  );

  const disabledAvatarUpdate = useMemo(() => {
    return !infoEditDisabled && isNormalGroup;
  }, [infoEditDisabled, isNormalGroup]);

  const inviteText = useMemo(() => {
    if (isGroupCreator) {
      return t('chat.createdGroupText');
    }
    if (!groupMyRole || !groupCreator) {
      return '';
    }
    if (groupMyRole?.join_mode === GroupMemberJoinMode.Invite) {
      return `${t('chat.addedYou', {
        name:
          groupInviter?.state === StateEnum.DELETE_ACCOUNT
            ? t('chat.deleteAccountText')
            : groupInviter?.name,
      })}`;
    }
    if (groupMyRole?.join_mode === GroupMemberJoinMode.Search) {
      return t('chat.joinedGroupBySearch');
    }
    if (groupMyRole?.join_mode === GroupMemberJoinMode.Scan) {
      return t('chat.joinedGroupByQRCode');
    }
    if (groupMyRole?.join_mode === GroupMemberJoinMode.Link) {
      return t('chat.joinedGroupByLink');
    }
    return t('chat.addedYou', {
      name:
        groupCreator.state === StateEnum.DELETE_ACCOUNT
          ? t('chat.deleteAccountText')
          : groupCreator?.name,
    });
  }, [groupInviter?.name, isGroupCreator, groupMyRole, groupCreator, t, groupInviter?.state]);

  if (!isGroup) return null;

  return (
    <Box
      className="MessageInviteCard fadeIn"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      w="395px"
      px="4"
      py="6"
      gap="4"
      mx="auto"
      bg="bg"
      borderRadius="xl"
      textAlign="center"
    >
      <Flex direction="column" alignItems="center" justifyContent="center">
        <Center>
          <UploadAvatar disabled={!disabledAvatarUpdate} onChange={handleAvatarUpdate}>
            <Box pos="relative">
              <Avatar src={session?.avatar} size="xl" activeShake={false} />
              {disabledAvatarUpdate && (
                <Flex
                  pos="absolute"
                  top="0"
                  left="0"
                  w="full"
                  h="full"
                  borderRadius="full"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                >
                  <EditRoundIcon />
                </Flex>
              )}
            </Box>
          </UploadAvatar>
        </Center>
        <Text fontSize="md" color="fg" fontWeight="semibold" pt="2" pb="1.5">
          {inviteText}
        </Text>
        {!isGroupCreator && (
          <Text fontSize="xs" color="fg.muted" fontWeight="medium">
            {groupCreator?.state === StateEnum.DELETE_ACCOUNT
              ? t('chat.deleteAccountText')
              : groupCreator?.name}
          </Text>
        )}
        <Text fontSize="xs" color="fg.muted" fontWeight="medium">
          {t('chat.groupText')} · {groupInfo?.member_num} {t('chat.groupMembersText')}
        </Text>
      </Flex>
      <Box px="4">
        {isNormalGroup ? (
          description ? (
            <ExpandableText
              buttonColor="colorPalette.solid"
              fontSize="sm"
              fontWeight="medium"
              text={description}
            >
              {description}
            </ExpandableText>
          ) : infoEditDisabled ? null : (
            <Text
              fontSize="sm"
              color="colorPalette.solid"
              fontWeight="medium"
              cursor="pointer"
              onClick={onAddDescription}
            >
              {t('chat.addDescription')}
            </Text>
          )
        ) : null}
      </Box>
      {/* {isNormalGroup && isStrangerInvite && (
        <Flex
          alignItems="center"
          gap="1"
          fontWeight="semibold"
          fontSize="sm"
          color="green.90"
          cursor="pointer"
          onClick={onSafetyToolsClick}
        >
          <SafetyToolsIcon />
          {t('chat.safetyTools')}
        </Flex>
      )} */}

      {isNormalGroup && (
        <>
          {isStrangerInvite ? (
            <Flex gap="4">
              <ButtonGray gap="1" flex="1" borderRadius="xl" onClick={onStayClick}>
                <Center fontSize="lg" color="green.100">
                  <SuccessIcon />
                </Center>
                {t('chat.stayGroup')}
              </ButtonGray>
              <ButtonGray gap="1" borderRadius="xl" flex="1" onClick={onExitGroupClick}>
                <Center fontSize="lg">
                  <LeaveIcon />
                </Center>
                {t('chat.leaveGroup')}
              </ButtonGray>
            </Flex>
          ) : (
            <Flex gap="4">
              {isGroupAdmin ? (
                <ButtonGray flex="1" borderRadius="xl" onClick={onSettingClick}>
                  <Center fontSize="lg">
                    <EditIcon />
                  </Center>
                  {t('chat.groupInfo')}
                </ButtonGray>
              ) : (
                <ButtonGray flex="1" borderRadius="xl" onClick={onGroupLinkClick}>
                  <Center fontSize="lg">
                    <LinkIcon />
                  </Center>
                  {t('chat.groupLinks')}
                </ButtonGray>
              )}
              <ButtonGray
                borderRadius="xl"
                flex="1"
                onClick={() => setIsOpen(true, { session: session!, groupInfo: groupInfo! })}
              >
                <Center fontSize="lg">
                  <AddPulsIcon />
                </Center>
                {t('action.addMembers')}
              </ButtonGray>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
});
